import { Button, Modal, useNotification } from '@prio365/prio365-react-library';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrioShareDto } from '../../../../models/PrioShare';
import { ProjectId } from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import PrioShareForm from './components/views/PrioShareForm';
import PrioShareDetailView from './components/views/PrioShareDetailView';
import { useCreatePrioShareDraft } from './hooks/useCreatePrioShareDraft';
import { useDeactivatePrioShare } from './hooks/useDeactivatePrioShare';
import { useRecreatePrioShare } from './hooks/useRecreatePrioShare';
import { PrioShareDraftProvider } from './PrioShareDraftContext';
import PrioShareSection from './PrioShareSection';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.application.background.default,
  },
  header: {
    padding: `${theme.spacing.small}px ${theme.spacing.regular}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

  },
  content: {
    flex: 1,
    padding: `${theme.spacing.small}px ${theme.spacing.regular}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.small,
    overflow: 'auto',
  },
}));

type PrioShareView = 'list' | 'create' | 'view';

interface PrioShareWidgetProps {
  projectId: ProjectId;
  projectExtensionId?: string;
  className?: string;
}

const PrioShareWidget: React.FC<PrioShareWidgetProps> = ({
  projectId,
  projectExtensionId,
  className,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t } = useTranslation();
  const [notification, notificationContextHolder] = useNotification();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [view, setView] = useState<PrioShareView>('list');
  const [selectedShare, setSelectedShare] = useState<PrioShareDto | null>(null);
  const [draft, setDraft] = useState<PrioShareDto | null>(null);

  const { mutateAsync: createDraft, isLoading: isCreatingDraft } =
    useCreatePrioShareDraft();
  const { mutate: deactivateShare } = useDeactivatePrioShare();
  const { mutateAsync: recreateShare, isLoading: isRecreating } =
    useRecreatePrioShare();
  const [deactivateModal, setDeactivateModal] = useState<{
    visible: boolean;
    item: PrioShareDto | null;
  }>({ visible: false, item: null });

  const headerTitle =
    view === 'create'
      ? t('common:widgetArea.prioShareWidget.createForm.title')
      : view === 'view' && selectedShare
        ? selectedShare.name
        : t('common:widgetArea.prioShareWidget.title');
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const startDraftCreation = useCallback(
    (prefill?: Partial<PrioShareDto>) => {
      if (!projectExtensionId || !projectId || projectId === 'me') return;
      setView('create');
      createDraft(projectExtensionId)
        .then((result) => {
          if (result.data) {
            setDraft(prefill ? { ...result.data, ...prefill } : result.data);
          } else {
            notification.error({
              title: t('common:widgetArea.prioShareWidget.createForm.draftError'),
            });
            setView('list');
          }
        })
        .catch(() => {
          notification.error({
            title: t('common:widgetArea.prioShareWidget.createForm.draftError'),
          });
          setView('list');
        });
    },
    [projectId, projectExtensionId, createDraft, notification, t]
  );

  const handleAddNew = useCallback(() => {
    startDraftCreation();
  }, [startDraftCreation]);

  const handleCancel = useCallback(() => {
    setDraft(null);
    setSelectedShare(null);
    setView('list');
  }, []);

  const handleCreated = useCallback(() => {
    setDraft(null);
    setView('list');
  }, []);

  const handleView = useCallback((item: PrioShareDto) => {
    setSelectedShare(item);
    setView('view');
  }, []);

  const handleEditDraft = useCallback((item: PrioShareDto) => {
    setDraft(item);
    setView('create');
  }, []);

  const handleRecreate = useCallback(
    (item: PrioShareDto) => {
      if (!projectExtensionId) return;
      recreateShare(
        { prioShareId: item.prioShareId, projectExtensionId },
        {
          onSuccess: (result) => {
            if (result.data) {
              setDraft(result.data);
              setView('create');
            } else {
              notification.error({
                title: t('common:widgetArea.prioShareWidget.recreateError'),
              });
            }
          },
          onError: () => {
            notification.error({
              title: t('common:widgetArea.prioShareWidget.recreateError'),
            });
          },
        }
      );
    },
    [projectExtensionId, recreateShare, notification, t]
  );

  const handleDeactivate = useCallback((item: PrioShareDto) => {
    setDeactivateModal({ visible: true, item });
  }, []);

  const handleDeactivateConfirm = useCallback(() => {
    const { item } = deactivateModal;
    if (!item || !projectExtensionId) return;
    setDeactivateModal({ visible: false, item: null });
    deactivateShare(
      { prioShareId: item.prioShareId, projectExtensionId },
      {
        onSuccess: () => {
          notification.success({
            title: t('common:widgetArea.prioShareWidget.deactivateSuccess'),
          });
          setSelectedShare(null);
          setView('list');
        },
        onError: () => {
          notification.error({
            title: t('common:widgetArea.prioShareWidget.deactivateError'),
          });
        },
      }
    );
  }, [deactivateModal, deactivateShare, projectExtensionId, notification, t]);
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  if (!projectExtensionId) return null;

  return (
    <div className={`${classes.root} ${className || ''}`}>
      {notificationContextHolder}
      <div className={classes.header}>
        <h2>{headerTitle}</h2>
        <Button
          iconProp={['fal', 'plus']}
          type="link"
          onClick={handleAddNew}
          loading={isCreatingDraft}
          style={view !== 'list' ? { visibility: 'hidden' } : undefined}
        />
      </div>
      <div className={classes.content}>
        {view === 'list' && (
          <PrioShareSection
            projectId={projectId}
            projectExtensionId={projectExtensionId}
            onView={handleView}
            onEditDraft={handleEditDraft}
          />
        )}
        {view === 'create' && (
          <PrioShareDraftProvider draft={draft} projectId={projectId} projectExtensionId={projectExtensionId}>
            <PrioShareForm
              onCancel={handleCancel}
              onCreated={handleCreated}
            />
          </PrioShareDraftProvider>
        )}
        {view === 'view' && selectedShare && (
          <PrioShareDetailView item={selectedShare} projectExtensionId={projectExtensionId} onBack={handleCancel} onDeactivate={handleDeactivate} onRecreate={handleRecreate} isRecreating={isRecreating} />
        )}
      </div>

      <Modal
        title={t('common:widgetArea.prioShareWidget.deactivateConfirm')}
        visible={deactivateModal.visible}
        onOk={handleDeactivateConfirm}
        onClose={() => setDeactivateModal({ visible: false, item: null })}
        okText={t('common:widgetArea.prioShareWidget.deactivateConfirmOk')}
        cancelText={t('common:cancel')}
        okButtonProps={{ type: 'danger' }}
      >
        {t('common:widgetArea.prioShareWidget.deactivateConfirmContent')}
      </Modal>
    </div>
  );
};

export default PrioShareWidget;
